import React from 'react';

import { ALERT_TYPES } from '@bringg/types';
import type { WayPoint, Customer } from '@bringg/types';

import {
	FleetCell,
	TimeCell,
	TimeWindowCell,
	WayPointTimeCell,
	RouteCell,
	LinkCell,
	StatusCell,
	TextCell,
	AlertNameCell,
	UserIdCell
} from '../cell-components';
import {
	getActiveWayPoint,
	getFirstStopWayPoint,
	getLastStopWayPoint,
	nullableObjectField,
	columnMapper,
	filterOutIds
} from './column-utils';
import { EXCEPTIONS_COLUMN_IDS } from '../../../consts';
import { sharedColumns } from './shared-columns';
import type { Exception, TranslatableOption } from '../../../types/common.consts';
import type { ColumnWithStrictId, ExceptionCellProps } from './column-utils';

// yet missing columns
// Planning Published, AD Status, Rank, Service Plan, Done Planning, Origin
// Order Tag, Price, Tag
export const taskAlertColumns: ColumnWithStrictId<Exception>[] = [
	// UI columns
	sharedColumns.selection as ColumnWithStrictId<Exception>,
	{
		translateKey: 'ALERT_NAME',
		id: EXCEPTIONS_COLUMN_IDS.ALERT_TYPE,
		accessor: 'alert_type',
		filter: 'arrayIncludes',
		Cell: ({ row, value: alertType }: ExceptionCellProps<ALERT_TYPES>) => {
			// to render the grouped cell label this cell is used
			// the grouped cell doesn't have `original` object
			return <AlertNameCell alertType={alertType} extraData={row?.original?.extra_data} />;
		}
	},
	{
		translateKey: 'ALERT_TIME',
		id: EXCEPTIONS_COLUMN_IDS.ALERT_TIME,
		accessor: 'alert_time',
		Cell: ({ row }: ExceptionCellProps) => <TimeCell dateKey="alert_time" alertId={row.original.id} />
	},
	{
		id: EXCEPTIONS_COLUMN_IDS.EXTERNAL_ID,
		accessor: 'external_id',
		translateKey: `ID`,
		Cell: ({ row }: ExceptionCellProps) => {
			if (row.isGrouped) {
				return row.groupByVal;
			}

			const { task_id, external_id } = row.original;
			return <LinkCell className="external-id-cell" taskId={task_id} externalId={external_id} />;
		},
		width: 200
	},
	sharedColumns.team,
	sharedColumns.alertAdditionalText,
	{
		translateKey: `SKILLS`,
		id: 'required_skills',
		// TODO: investigate how accessor affects hook processing time
		// accessor: (r) => r.required_skills?.join(', ') || '',
		// TODO: investigate what is going inside the TableInstance rows data, doubt that it is good idea to have there observables
		Cell: ({ row }: ExceptionCellProps) => row.original.required_skills?.join(', ') || null,
		disableGroupBy: true,
		disableFilters: true,
		disableSortBy: true,
		disableGlobalFilter: true
	},
	{
		id: EXCEPTIONS_COLUMN_IDS.USER_ID,
		translateKey: 'ASSIGNED_TO',
		accessor: 'user_id',
		filter: 'boolean',
		Cell: ({ value, row: { isGrouped, original } }: ExceptionCellProps<number>) => {
			return isGrouped ? null : <UserIdCell taskId={original.task_id} driverId={value} />;
		}
	},
	{
		translateKey: 'ROUTE',
		id: EXCEPTIONS_COLUMN_IDS.ROUTE,
		accessor: 'routeTitle',
		// accessor for data is - routeTitle - used for sort and display data
		// meanwhile col.id is - routeId - used for data filter(the filters values is routeId since they are uniq)
		filter: 'arrayIncludes',
		Cell: ({ value, row }: ExceptionCellProps) => {
			return <RouteCell routeId={row?.original?.routeId} routeName={value} />;
		}
	},
	{
		translateKey: 'FLEET',
		accessor: 'fleet_id',
		Cell: ({ value }: ExceptionCellProps<number>) => <FleetCell fleetId={value} />
	},
	{
		translateKey: 'TIME_WINDOW',
		id: 'time_window',
		width: 350,
		disableGroupBy: true,
		disableFilters: true,
		disableSortBy: true,
		disableGlobalFilter: true,
		Cell: ({ row }: ExceptionCellProps) => <TimeWindowCell alertId={row.original.id} />
	},
	{
		translateKey: 'STATUS',
		accessor: 'status',
		Cell: ({ value, row }: ExceptionCellProps<number>) => !row.isGrouped && <StatusCell status={value} />
	},
	{
		translateKey: 'PRIORITY',
		accessor: 'priority'
	},
	{
		id: EXCEPTIONS_COLUMN_IDS.TITLE,
		translateKey: 'ORDER',
		accessor: 'title',
		filter: 'text'
	},
	{
		// Think of more informative key like order_created_at
		translateKey: 'ORDER_CREATED',
		accessor: 'created_at',
		width: 300,
		Cell: ({ row }: ExceptionCellProps) => <TimeCell dateKey="created_at" alertId={row.original.id} />
	},
	{
		// Think of more informative key like order_scheduled_at
		translateKey: 'SCHEDULED_FOR',
		accessor: 'scheduled_at',
		width: 300,
		Cell: ({ row }: ExceptionCellProps) => <TimeCell dateKey="scheduled_at" alertId={row.original.id} />
	},
	{
		translateKey: 'NEXT_STOP',
		id: 'next_stop',
		disableSortBy: true,
		width: 400,
		Cell: ({ row }: ExceptionCellProps) => {
			const label = nullableObjectField<WayPoint>(getActiveWayPoint(row.original), 'address');

			return <TextCell text={label} />;
		}
	},

	// First Stop Data
	{
		translateKey: 'ADDRESS_FS',
		id: 'address_first_stop',
		disableSortBy: true,
		width: 350,
		Cell: ({ row }: ExceptionCellProps) => {
			const label = nullableObjectField<WayPoint>(getFirstStopWayPoint(row.original), 'address');

			return <TextCell text={label} />;
		}
	},
	{
		translateKey: 'FULL_ADDRESS_FS',
		id: 'full_address_first_stop',
		width: 450,
		disableSortBy: true,
		Cell: ({ row }: ExceptionCellProps) => {
			const label = nullableObjectField<WayPoint>(
				getFirstStopWayPoint(row.original),
				// full_address is present on way_point data transferred from back-end, maybe need to update @types
				// @ts-ignore
				'full_address'
			);

			return <TextCell text={label} />;
		}
	},
	{
		translateKey: 'ADDRESS_TYPE_FS',
		id: 'address_type_first_stop',
		width: 220,
		disableSortBy: true,
		Cell: ({ row }: ExceptionCellProps) => {
			const label = nullableObjectField<WayPoint>(getFirstStopWayPoint(row.original), 'address_type');

			return <TextCell text={label} />;
		}
	},
	{
		translateKey: 'CUSTOMER_FS',
		id: 'customer_first_stop',
		disableSortBy: true,
		Cell: ({ row }: ExceptionCellProps) => {
			const label = nullableObjectField<Customer>(getFirstStopWayPoint(row.original)?.customer, 'name');

			return <TextCell text={label} />;
		}
	},
	{
		translateKey: 'SCHEDULED_AT_FS',
		id: 'scheduled_at_first_stop',
		width: 300,
		disableSortBy: true,
		Cell: ({ row }: ExceptionCellProps) => (
			<WayPointTimeCell alertId={row.original.id} wayPointId="first" dateKey="scheduled_at" />
		)
	},
	{
		translateKey: 'ETL_FS',
		id: 'etl_first_stop',
		width: 300,
		disableSortBy: true,
		Cell: ({ row }: ExceptionCellProps) => (
			<WayPointTimeCell alertId={row.original.id} wayPointId="first" dateKey="etl" />
		)
	},
	{
		translateKey: 'ETA_FS',
		id: 'eta_first_stop',
		width: 300,
		disableSortBy: true,
		Cell: ({ row }: ExceptionCellProps) => (
			<WayPointTimeCell alertId={row.original.id} wayPointId="first" dateKey="eta" />
		)
	},
	{
		translateKey: 'PHONE_FS',
		id: 'phone_first_stop',
		disableSortBy: true,
		Cell: ({ row }: ExceptionCellProps) => {
			const label = nullableObjectField<WayPoint>(getFirstStopWayPoint(row.original), 'phone');

			return <TextCell text={label} />;
		}
	},
	{
		translateKey: 'ZIP_CODE_FS',
		id: 'zip_code_first_stop',
		disableSortBy: true,
		Cell: ({ row }: ExceptionCellProps) => {
			const label = nullableObjectField<WayPoint>(getFirstStopWayPoint(row.original), 'zipcode');

			return <TextCell text={label} />;
		}
	},

	// Last Stop Data
	{
		translateKey: 'ADDRESS_LS',
		id: 'address_last_stop',
		width: 300,
		disableSortBy: true,
		Cell: ({ row }: ExceptionCellProps) => {
			const label = nullableObjectField<WayPoint>(getLastStopWayPoint(row.original), 'address');

			return <TextCell text={label} />;
		}
	},
	{
		translateKey: 'FULL_ADDRESS_LS',
		id: 'full_address_last_stop',
		width: 450,
		disableSortBy: true,
		Cell: ({ row }: ExceptionCellProps) => {
			const label = nullableObjectField<WayPoint>(
				getLastStopWayPoint(row.original),
				// @ts-ignore
				'full_address'
			);

			return <TextCell text={label} />;
		}
	},
	{
		translateKey: 'ADDRESS_TYPE_LS',
		id: 'address_type_last_stop',
		disableSortBy: true,
		Cell: ({ row }: ExceptionCellProps) => {
			const label = nullableObjectField<WayPoint>(getLastStopWayPoint(row.original), 'address_type');

			return <TextCell text={label} />;
		}
	},
	{
		translateKey: 'CUSTOMER_LS',
		id: 'customer_last_stop',
		disableSortBy: true,
		Cell: ({ row }: ExceptionCellProps) => {
			const label = nullableObjectField<Customer>(getLastStopWayPoint(row.original)?.customer, 'name');

			return <TextCell text={label} />;
		}
	},
	{
		translateKey: 'SCHEDULED_AT_LS',
		id: 'scheduled_at_last_stop',
		width: 300,
		disableSortBy: true,
		Cell: ({ row }: ExceptionCellProps) => (
			<WayPointTimeCell alertId={row.original.id} wayPointId="last" dateKey="scheduled_at" />
		)
	},
	{
		translateKey: 'ETL_LS',
		id: 'etl_last_stop',
		width: 300,
		disableSortBy: true,
		Cell: ({ row }: ExceptionCellProps) => (
			<WayPointTimeCell alertId={row.original.id} wayPointId="last" dateKey="etl" />
		)
	},
	{
		translateKey: 'ETA_LS',
		id: 'eta_last_stop',
		width: 300,
		disableSortBy: true,
		Cell: ({ row }: ExceptionCellProps) => (
			<WayPointTimeCell alertId={row.original.id} wayPointId="last" dateKey="eta" />
		)
	},
	{
		translateKey: 'PHONE_LS',
		id: 'phone_last_stop',
		disableSortBy: true,
		Cell: ({ row }: ExceptionCellProps) => {
			const label = nullableObjectField<WayPoint>(getLastStopWayPoint(row.original), 'phone');
			return <TextCell text={label} />;
		}
	},
	{
		translateKey: 'ZIP_CODE_LS',
		id: 'zip_code_last_stop',
		disableSortBy: true,
		Cell: ({ row }: ExceptionCellProps) => {
			const label = nullableObjectField<WayPoint>(getLastStopWayPoint(row.original), 'zipcode');
			return <TextCell text={label} />;
		}
	},
	{
		translateKey: 'TOTAL_WEIGHT',
		id: 'total_weight',
		accessor: 'aggregations.max_weight'
	},
	sharedColumns.severity,
	sharedColumns.actions,
	sharedColumns.alertOwner,
	sharedColumns.company
].map(
	// @ts-ignore weird behavior I wasn't able to fix
	columnMapper
);

export const defaultOrderedColumnOptions: TranslatableOption[] = taskAlertColumns
	.filter(({ id }) => !filterOutIds.has(id))
	.map(({ id, translateKey }) => ({ id, translateKey }));

export const taskAlertColumnsTranslateKeys = taskAlertColumns.reduce((acc, column) => {
	acc.set(column.id, column.translateKey);
	return acc;
}, new Map());
